import { Controller } from '@hotwired/stimulus';

/**
 * File Dropzone Controller
 *
 * Manages a rich file dropzone with three visual states:
 * placeholder, preview, and error. Dispatches custom events
 * for parent controllers to react to file selection.
 *
 * Values:
 * - maxSize: Maximum file size in bytes (default: 10485760 = 10 Mo)
 * - allowedTypes: Accepted MIME types (default: PDF, JPEG, PNG)
 * - formatsLabel: Server-derived French label of the accepted formats
 *   (e.g. "PDF" or "PDF, JPEG ou PNG") used in the type-rejection
 *   message (default: "PDF, JPEG ou PNG" for callers not emitting the attr)
 * - multiple: Allow multi-file selection/drops (default: false)
 *
 * Events dispatched:
 * - file-dropzone:selected (detail: { file })
 * - file-dropzone:cleared
 */
export default class extends Controller {
  static targets = [
    'dropzone',
    'input',
    'placeholder',
    'preview',
    'error',
    'filename',
    'filesize',
    'errorMessage',
  ];

  static values = {
    maxSize: { type: Number, default: 10485760 },
    allowedTypes: { type: Array, default: ['application/pdf', 'image/jpeg', 'image/png'] },
    // Server-derived French label of the accepted formats (e.g. "PDF"),
    // emitted by Form::DropzoneComponent#formats_label. The default covers
    // callers not emitting the attr (raw dropzones, old cached HTML) and
    // matches the historical hard-coded copy.
    formatsLabel: { type: String, default: 'PDF, JPEG ou PNG' },
    // When true, allow Cmd/Ctrl-click multi-selection in the native picker
    // and accept multi-file drops. Preview text becomes "N fichiers sélectionnés".
    multiple: { type: Boolean, default: false },
  };

  connect() {
    this._boundFileSelected = this.fileSelected.bind(this);
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener('change', this._boundFileSelected);
    }
  }

  disconnect() {
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener('change', this._boundFileSelected);
    }
  }

  // -- Drag and drop --

  dragover(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.hasDropzoneTarget) return;
    this.dropzoneTarget.classList.add('border-primary-500', 'bg-primary-50/50');
    this.dropzoneTarget.classList.remove('border-neutral-300', 'border-dashed');
  }

  dragleave(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.hasDropzoneTarget) return;
    this.dropzoneTarget.classList.remove('border-primary-500', 'bg-primary-50/50');
    this.dropzoneTarget.classList.add('border-neutral-300', 'border-dashed');
  }

  drop(event) {
    event.preventDefault();
    event.stopPropagation();
    this._resetDropzoneClasses();

    const files = event.dataTransfer.files;
    if (files.length === 0 || !this.hasInputTarget) return;

    if (this.multipleValue) {
      // Multi mode — accept all dropped files (after per-file validation).
      // Server is the source of truth for partial-failure reporting; we still
      // reject the whole batch client-side if any file is invalid because
      // there's no per-file error UI here.
      for (const f of files) {
        const error = this._validateFile(f);
        if (error) {
          this._showError(error);
          return;
        }
      }
      this.inputTarget.files = files;
      this._showMultiPreview(files);
      return;
    }

    const file = files[0];
    const error = this._validateFile(file);
    if (error) {
      this._showError(error);
      return;
    }

    // Enforce single file
    const dt = new DataTransfer();
    dt.items.add(file);
    this.inputTarget.files = dt.files;
    this._showPreview(file);
  }

  // -- File selection --

  fileSelected(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (this.multipleValue && files.length > 1) {
      for (const f of files) {
        const error = this._validateFile(f);
        if (error) {
          this._showError(error);
          this.inputTarget.value = '';
          return;
        }
      }
      this._showMultiPreview(files);
      return;
    }

    const file = files[0];
    const error = this._validateFile(file);
    if (error) {
      this._showError(error);
      this.inputTarget.value = '';
      return;
    }

    this._showPreview(file);
  }

  // -- Clear / reset --

  clear(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.hasInputTarget) this.inputTarget.value = '';
    this._showPlaceholder();
    this._resetDropzoneClasses();

    this.dispatch('cleared', { bubbles: true });
  }

  // -- Validation --

  _validateFile(file) {
    if (file.size > this.maxSizeValue) {
      const sizeMo = (file.size / 1048576).toFixed(1);
      const maxMo = (this.maxSizeValue / 1048576).toFixed(0);
      return `Fichier trop volumineux (${sizeMo} Mo). Maximum : ${maxMo} Mo.`;
    }

    if (this.allowedTypesValue.length > 0 && !this.allowedTypesValue.includes(file.type)) {
      return `Format non supporté. Utilisez ${this.formatsLabelValue}.`;
    }

    return null;
  }

  // -- State transitions --

  _showPlaceholder() {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden');
    if (this.hasPreviewTarget) this.previewTarget.classList.add('hidden');
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden');
  }

  _showPreview(file) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden');
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden');
    if (this.hasPreviewTarget) this.previewTarget.classList.remove('hidden');

    if (this.hasFilenameTarget) this.filenameTarget.textContent = file.name;
    if (this.hasFilesizeTarget) this.filesizeTarget.textContent = this._formatSize(file.size);

    // Success border
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('border-success-500', 'bg-success-50/50');
      this.dropzoneTarget.classList.remove('border-neutral-300', 'border-dashed');
    }

    this.dispatch('selected', { detail: { file }, bubbles: true });
  }

  _showMultiPreview(files) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden');
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden');
    if (this.hasPreviewTarget) this.previewTarget.classList.remove('hidden');

    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = `${files.length} fichiers sélectionnés`;
    }
    if (this.hasFilesizeTarget) {
      const total = Array.from(files).reduce((sum, f) => sum + f.size, 0);
      this.filesizeTarget.textContent = this._formatSize(total);
    }

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('border-success-500', 'bg-success-50/50');
      this.dropzoneTarget.classList.remove('border-neutral-300', 'border-dashed');
    }

    // Re-use `selected` event so the parent `document-upload` controller sees
    // the batch as a ready submission (the auto-submit flow doesn't care
    // about per-file detail). Single-file callers still receive { file }.
    this.dispatch('selected', { detail: { files: Array.from(files) }, bubbles: true });
  }

  _showError(message) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden');
    if (this.hasPreviewTarget) this.previewTarget.classList.add('hidden');
    if (this.hasErrorTarget) this.errorTarget.classList.remove('hidden');
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message;

    // Error border
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('border-danger-500', 'bg-danger-50/50');
      this.dropzoneTarget.classList.remove('border-neutral-300', 'border-dashed');
    }

    this.dispatch('cleared', { bubbles: true });
  }

  _resetDropzoneClasses() {
    if (!this.hasDropzoneTarget) return;
    this.dropzoneTarget.classList.remove(
      'border-primary-500',
      'bg-primary-50/50',
      'border-success-500',
      'bg-success-50/50',
      'border-danger-500',
      'bg-danger-50/50'
    );
    this.dropzoneTarget.classList.add('border-neutral-300', 'border-dashed');
  }

  _formatSize(bytes) {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
  }
}
